import { IonIcon } from '@ionic/react';
import { carOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import type { OrderStatus } from '../../types/order';
import { useRootFontScale } from '../../hooks/useRootFontScale';
import { STEP_ICON, STEP_COLOR } from './orderStepVisuals';

// Text-scale cutoff: at/above this root-font multiplier the four ¼-width labels
// crowd together, so we show the label under the CURRENT step only (the rest are
// icon-only). Below it, every step keeps its full label as normal. Tune here if
// the switch feels too early/late. (CSS can't branch on text scale — see
// `useRootFontScale`.)
const COLLAPSE_LABELS_AT_SCALE = 1.2;

const PICKUP_STEPS: OrderStatus[] = [
  'paid',
  'accepted',
  'ready_to_pickup',
  'completed',
];

function stepIndex(steps: OrderStatus[], status: OrderStatus): number {
  return steps.indexOf(status);
}

interface OrderStatusBreadcrumbProps {
  status: OrderStatus;
  /** Drop-off relabels the ready step: for the eater it means the chef's run
   *  has started, so "Ready" (as in come get it) would be wrong. */
  fulfillmentMethod?: string;
}

export const OrderStatusBreadcrumb: React.FC<OrderStatusBreadcrumbProps> = ({
  status,
  fulfillmentMethod,
}) => {
  // Status labels are translated via the orders namespace; the
  // ts-string keys (paid, ready_to_pickup, etc.) ARE the BE wire
  // values, so referencing them directly keeps lookup safe.
  const { t } = useTranslation('orders');
  // Only collapse to the current-step label once text is large enough to crowd
  // the four columns; normal sizes keep every label.
  const collapseLabels = useRootFontScale() >= COLLAPSE_LABELS_AT_SCALE;
  if (status === 'canceled' || status === 'abandoned' || status === 'expired' || status === 'no_show') {
    return (
      <div className="flex justify-center">
        <span className="rounded-full bg-brand-danger/10 px-4 py-1.5 text-sm font-semibold text-brand-danger">
          {t(`status.${status}`)}
        </span>
      </div>
    );
  }

  const steps = PICKUP_STEPS;
  const isDropoff = fulfillmentMethod === 'dropoff';
  const stepLabel = (step: OrderStatus) =>
    isDropoff && step === 'ready_to_pickup' ? t('status.out_for_delivery') : t(`status.${step}`);
  const displayStatus = status === 'received' ? 'paid' : status;
  const currentIdx = stepIndex(steps, displayStatus);

  return (
    <div className="flex items-start">
      {steps.map((step, i) => {
        const reached = currentIdx >= i;
        const isCurrent = currentIdx === i;
        const lineReached = currentIdx > i;
        const color = STEP_COLOR[step];
        return (
          <div key={step} className="flex flex-1 flex-col items-center">
            <div className="relative flex h-10 w-full items-center">
              {/* Left connector */}
              {i > 0 && (
                <div
                  className={`h-0.5 flex-1 ${reached ? color?.line : 'bg-border'}`}
                />
              )}
              {i === 0 && <div className="flex-1" />}

              {/* Invisible spacer to reserve connector space */}
              <div className={`shrink-0 ${isCurrent ? 'w-10' : 'w-7'}`} />

              {/* Step circle with icon — absolutely positioned to stay centered on the line */}
              <div
                className={`absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 ${
                  isCurrent ? 'h-10 w-10' : 'h-7 w-7'
                } ${
                  reached
                    ? `${color?.border} ${color?.bg}`
                    : 'border-border bg-background'
                } ${isCurrent ? `ring-2 ${color?.ring}` : ''}`}
              >
                <IonIcon
                  icon={isDropoff && step === 'ready_to_pickup' ? carOutline : STEP_ICON[step]}
                  className={`text-sm ${reached ? 'text-white' : 'text-muted-foreground'}`}
                />
              </div>

              {/* Right connector */}
              {i < steps.length - 1 && (
                <div
                  className={`h-0.5 flex-1 ${lineReached ? STEP_COLOR[steps[i + 1]]?.line : 'bg-border'}`}
                />
              )}
              {i === steps.length - 1 && <div className="flex-1" />}
            </div>

            {/* Label — at large text only the current step keeps its label so
                the columns don't crowd; all steps are labeled at normal sizes. */}
            {(!collapseLabels || isCurrent) && (
              <span
                className={`mt-1 text-center text-xs leading-tight ${
                  reached ? `font-semibold ${color?.text}` : 'text-muted-foreground'
                }`}
              >
                {stepLabel(step)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};
